import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Container,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha, darken } from '@mui/material/styles';
import { Check, CheckCircle2, Image, Send, Shield, ShieldAlert, Trophy, UserMinus, UserPlus, Users, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  getFriendRequests,
  getFriends,
  removeFriend,
  respondToFriendRequest,
  sendFriendRequest,
} from '../utils/api';
import { LoadingIndicator } from '../components/assets';
import { useNotifications } from '../contexts/NotificationsContext';

const getFriendMetrics = (user = {}) => ({
  username: user.username || 'Unknown friend',
  score: user.score ?? user.points ?? 0,
  completedTasks: user.completed_tasks ?? 0,
});

const compactNumberFormatter = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: 1,
});

const fullNumberFormatter = new Intl.NumberFormat('en-US');

const formatMetricValue = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? compactNumberFormatter.format(numericValue) : String(value);
};

const formatFullMetricValue = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? fullNumberFormatter.format(numericValue) : String(value);
};

const Friends = () => {
  const [friends, setFriends] = useState([]);
  const [incoming, setIncoming] = useState([]);
  const [outgoing, setOutgoing] = useState([]);
  const [username, setUsername] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { setIncomingFriendRequests } = useNotifications();

  const loadFriends = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [friendsData, requestsData] = await Promise.all([
        getFriends(),
        getFriendRequests(),
      ]);
      setFriends(friendsData.friends || []);
      setIncoming(requestsData.incoming || []);
      setOutgoing(requestsData.outgoing || []);
      setIncomingFriendRequests((requestsData.incoming || []).length);
    } catch (err) {
      console.error(err);
      setError(err.data?.message || err.message || 'Failed to load friends.');
    } finally {
      setLoading(false);
    }
  }, [setIncomingFriendRequests]);

  useEffect(() => {
    loadFriends();
  }, [loadFriends]);

  const handleSendRequest = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      await sendFriendRequest(username);
      setUsername('');
      setMessage('Friend request sent.');
      await loadFriends();
    } catch (err) {
      console.error(err);
      setError(err.data?.message || err.message || 'Failed to send request.');
    } finally {
      setSaving(false);
    }
  };

  const handleRespond = async (requestId, status) => {
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      await respondToFriendRequest(requestId, status);
      setMessage(status === 'accepted' ? 'Friend added.' : 'Request declined.');
      await loadFriends();
    } catch (err) {
      console.error(err);
      setError(err.data?.message || err.message || 'Failed to update request.');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveFriend = async (friendUserId, friendUsername) => {
    const confirmed = window.confirm(`Remove ${friendUsername} from your friends?`);
    if (!confirmed) return;

    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      await removeFriend(friendUserId);
      setMessage('Friend removed.');
      await loadFriends();
    } catch (err) {
      console.error(err);
      setError(err.data?.message || err.message || 'Failed to remove friend.');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenFriendGallery = (friend) => {
    navigate(`/${encodeURIComponent(friend.username)}/gallery`);
  };

  const panelShadow = (theme, intensity = 0.6) => (
    `0 8px 15px ${alpha(theme.palette.secondary.main, intensity)}, ` +
    `0 20px 40px ${alpha(theme.palette.secondary.main, intensity / 2)}, ` +
    `0 35px 65px ${alpha(theme.palette.secondary.main, intensity / 6)}`
  );

  const arcadePanelSx = (theme, intensity = 0.6) => ({
    background: `linear-gradient(145deg, ${alpha(theme.palette.background.paper, 0.96)} 0%, ${theme.palette.background.default} 72%, ${alpha(theme.palette.primary.main, 0.08)} 100%)`,
    border: `3px solid ${theme.palette.green.main}`,
    borderRadius: 4,
    boxShadow: panelShadow(theme, intensity),
  });

  const renderEmpty = (text) => (
    <Box
      sx={(theme) => ({
        ...arcadePanelSx(theme, 0.25),
        px: 3,
        py: 4,
        textAlign: 'center',
      })}
    >
      <Typography variant="body2" color="text.secondary">
        {text}
      </Typography>
    </Box>
  );

  const renderSectionTitle = (Icon, label, count) => (
    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2.5 }}>
      <Box
        sx={(theme) => ({
          width: 42,
          height: 42,
          display: 'grid',
          placeItems: 'center',
          color: theme.palette.primary.main,
          filter: `drop-shadow(0 0 10px ${alpha(theme.palette.primary.main, 0.7)})`,
        })}
      >
        <Icon size={34} />
      </Box>
      <Typography
        variant="h6"
        sx={(theme) => ({
          color: theme.palette.secondary.main,
          overflowWrap: 'anywhere',
          textTransform: 'uppercase',
        })}
      >
        {label} [{count}]
      </Typography>
    </Stack>
  );

  const renderAvatar = (user = {}, size = 80) => {
    const imageUrl = user.profile_image_url || '/dope_logo_192.png';
    const isDefaultLogo = !user.profile_image_url;

    return (
      <Box
        sx={(theme) => ({
          width: size,
          height: size,
          flex: '0 0 auto',
          borderRadius: '50%',
          bgcolor: theme.palette.background.default,
          border: `4px solid ${theme.palette.primary.main}`,
          boxShadow: `4px 4px 0 ${theme.palette.primary.main}`,
          display: 'grid',
          placeItems: 'center',
          p: 0.75,
        })}
      >
        <Box
          component="img"
          src={imageUrl}
          alt={`${getFriendMetrics(user).username}'s profile`}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: isDefaultLogo ? 'contain' : 'cover',
            borderRadius: '50%',
            display: 'block',
          }}
        />
      </Box>
    );
  };

  const renderInlineMetric = (Icon, label, value) => (
    <Box
      title={`${label}: ${formatFullMetricValue(value)}`}
      sx={(theme) => ({
        minWidth: 0,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        borderRadius: 10,
        bgcolor: alpha(theme.palette.background.default, 0.72),
        border: `1px solid ${alpha(theme.palette.primary.main, 0.35)}`,
        px: { xs: 1, sm: 1.5 },
        py: 1,
      })}
    >
      <Box sx={{ color: 'green.main', display: 'flex', flexShrink: 0 }}>
        <Icon size={18} aria-hidden="true" />
      </Box>
      <Typography
        aria-label={`${label}: ${formatFullMetricValue(value)}`}
        noWrap
        sx={{
          color: 'text.primary',
          flexShrink: 0,
          fontSize: { xs: '0.72rem', sm: '0.9rem' },
        }}
      >
        {formatMetricValue(value)}
      </Typography>
    </Box>
  );

  return (
    <Container maxWidth="lg" sx={{ pb: 14 }}>
      <Stack spacing={5}>
        <Box
          sx={(theme) => ({
            ...arcadePanelSx(theme, 0.35),
            p: { xs: 2, sm: 3 },
          })}
        >
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems={{ md: 'center' }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                <Users size={34} />
                <Typography variant="h4" sx={{ color: 'primary.main', overflowWrap: 'anywhere' }}>
                  Friends
                </Typography>
              </Stack>
              <Typography variant="body2" sx={(theme) => ({ color: alpha(theme.palette.text.primary, 0.72) })}>
                Enter a username to send a friend request.
              </Typography>
            </Box>

            <Box component="form" onSubmit={handleSendRequest} sx={{ flex: 1, width: '100%' }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <TextField
                  label="Username"
                  value={username}
                  onChange={(event) => setUsername(event.target.value)}
                  disabled={saving}
                  fullWidth
                />
                <Button
                  type="submit"
                  variant="contained"
                  disabled={saving || !username.trim()}
                  startIcon={<UserPlus size={18} />}
                  sx={(theme) => ({
                    minWidth: 164,
                    bgcolor: alpha(theme.palette.secondary.main, 0.9),
                    color: theme.palette.text.primary,
                    borderColor: theme.palette.secondary.main,
                    boxShadow: `0 6px 0 ${darken(theme.palette.secondary.main, 0.46)}, 0 0 14px ${alpha(theme.palette.secondary.main, 0.35)}`,
                    transition: 'transform 120ms ease, box-shadow 120ms ease, background-color 120ms ease',
                    '&:hover': {
                      bgcolor: theme.palette.primary.main,
                      color: theme.palette.background.default,
                      borderColor: theme.palette.primary.main,
                      boxShadow: `0 8px 0 ${darken(theme.palette.primary.main, 0.47)}, 0 0 18px ${alpha(theme.palette.primary.main, 0.5)}`,
                      transform: 'translate(-2px, -2px)',
                    },
                    '&:active': {
                      boxShadow: `0 2px 0 ${darken(theme.palette.primary.main, 0.47)}`,
                      transform: 'translateY(4px)',
                    },
                  })}
                >
                  Add Friend
                </Button>
              </Stack>
            </Box>
          </Stack>
        </Box>

        {message && <Alert severity="success">{message}</Alert>}
        {error && <Alert severity="error">{error}</Alert>}

        {loading ? (
          <LoadingIndicator text="loading friends" />
        ) : (
          <Stack spacing={5}>
            {incoming.length > 0 && (
              <Box component="section">
                {renderSectionTitle(ShieldAlert, 'Friend Requests', incoming.length)}
                <Grid container spacing={3}>
                  {incoming.map((request) => {
                    const friend = getFriendMetrics(request.user);

                    return (
                      <Grid size={{ xs: 12, md: 6 }} key={request.id}>
                        <Box
                          sx={(theme) => ({
                            ...arcadePanelSx(theme, 0.8),
                            p: { xs: 2, sm: 2.5 },
                            height: '100%',
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 2,
                            transition: 'transform 160ms ease, border-color 160ms ease',
                            '&:hover': {
                              transform: 'translateY(-4px)',
                              borderColor: theme.palette.text.primary,
                            },
                          })}
                        >
                          <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0 }}>
                            {renderAvatar(request.user, 80)}
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="h6" sx={{ color: 'primary.main', overflowWrap: 'anywhere' }}>
                                {friend.username}
                              </Typography>
                              <Typography variant="body2" sx={(theme) => ({ color: alpha(theme.palette.text.primary, 0.62) })}>
                                Points {friend.score}
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'secondary.main', fontWeight: 700 }}>
                                {friend.completedTasks} tasks complete
                              </Typography>
                            </Box>
                          </Stack>

                          <Stack direction="row" spacing={1.25} justifyContent="center">
                            <IconButton
                              aria-label={`Accept ${friend.username}`}
                              onClick={() => handleRespond(request.id, 'accepted')}
                              disabled={saving}
                              sx={(theme) => ({
                                width: 48,
                                height: 48,
                                bgcolor: alpha(theme.palette.primary.main, 0.18),
                                color: theme.palette.primary.main,
                                '&:hover': {
                                  bgcolor: theme.palette.primary.main,
                                  color: theme.palette.background.default,
                                },
                              })}
                            >
                              <Check size={24} />
                            </IconButton>
                            <IconButton
                              aria-label={`Decline ${friend.username}`}
                              onClick={() => handleRespond(request.id, 'declined')}
                              disabled={saving}
                              sx={(theme) => ({
                                width: 48,
                                height: 48,
                                bgcolor: alpha(theme.palette.secondary.main, 0.18),
                                color: theme.palette.secondary.main,
                                '&:hover': {
                                  bgcolor: theme.palette.secondary.main,
                                  color: theme.palette.text.primary,
                                },
                              })}
                            >
                              <X size={24} />
                            </IconButton>
                          </Stack>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            )}

            <Box component="section">
              {renderSectionTitle(Shield, 'Friends', friends.length)}
              {friends.length ? (
                <Grid container spacing={2}>
                  {friends.map((friend) => {
                    const metrics = getFriendMetrics(friend);
                    const imageUrl = friend.profile_image_url || '/dope_logo_192.png';
                    const isDefaultLogo = !friend.profile_image_url;

                    return (
                      <Grid size={12} key={friend.id} sx={{ display: 'flex' }}>
                        <Box
                          data-testid="friend-card"
                          role="link"
                          tabIndex={0}
                          aria-label={`Open ${friend.username}'s reward gallery`}
                          title={`Open ${friend.username}'s reward gallery`}
                          onClick={() => handleOpenFriendGallery(friend)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              handleOpenFriendGallery(friend);
                            }
                          }}
                          sx={(theme) => ({
                            ...arcadePanelSx(theme, 0.6),
                            position: 'relative',
                            width: '100%',
                            height: { xs: 150, sm: 180 },
                            overflow: 'hidden',
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'pointer',
                            transition: 'transform 160ms ease, border-color 160ms ease, box-shadow 160ms ease',
                            '&:focus-visible': {
                              outline: `3px solid ${theme.palette.primary.main}`,
                              outlineOffset: 4,
                            },
                            '&:hover': {
                              transform: 'translate(-3px, -3px)',
                              borderColor: theme.palette.green.main,
                              boxShadow: `7px 7px 0 ${alpha(theme.palette.green.main, 0.38)}, ${panelShadow(theme, 0.75)}`,
                            },
                            '&:hover .friend-profile-image': {
                              transform: 'scale(1.06)',
                            },
                            '&:hover .gallery-cue': {
                              transform: 'translateX(3px)',
                              color: theme.palette.green.main,
                            },
                          })}
                        >
                          <Box
                            data-testid="friend-profile-backdrop"
                            sx={(theme) => ({
                              position: 'absolute',
                              inset: '0 auto 0 0',
                              width: { xs: '52%', sm: 270 },
                              bgcolor: theme.palette.background.default,
                              overflow: 'hidden',
                              WebkitMaskImage: 'linear-gradient(to right, #000 58%, transparent 100%)',
                              maskImage: 'linear-gradient(to right, #000 58%, transparent 100%)',
                            })}
                          >
                            <Box
                              className="friend-profile-image"
                              component="img"
                              src={imageUrl}
                              alt={`${metrics.username}'s profile`}
                              sx={{
                                width: '100%',
                                height: '100%',
                                objectFit: isDefaultLogo ? 'contain' : 'cover',
                                objectPosition: 'left center',
                                display: 'block',
                                transition: 'transform 220ms ease',
                              }}
                            />
                          </Box>

                          <Box
                            sx={{
                              position: 'relative',
                              zIndex: 1,
                              ml: { xs: '30%', sm: '190px' },
                              py: { xs: 1.5, sm: 2.5 },
                              pr: { xs: 7, sm: 12 },
                              minWidth: 0,
                              flex: 1,
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'flex-start',
                              justifyContent: 'center',
                              gap: { xs: 1.25, sm: 2 },
                            }}
                          >
                            <Typography
                              variant="h6"
                              title={metrics.username}
                              sx={(theme) => ({
                                color: 'primary.main',
                                width: '100%',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                flex: 1,
                                minWidth: 0,
                                textShadow: `0 0 12px ${alpha(theme.palette.primary.main, 0.55)}`,
                              })}
                            >
                              {metrics.username}
                            </Typography>

                            <Stack
                              direction={{ xs: 'column', sm: 'row' }}
                              spacing={1}
                              sx={{ alignItems: 'flex-start', flexShrink: 0 }}
                            >
                              {renderInlineMetric(Trophy, 'Points', metrics.score)}
                              {renderInlineMetric(CheckCircle2, 'Tasks done', metrics.completedTasks)}
                            </Stack>
                          </Box>

                          <Box
                            className="gallery-cue"
                            aria-hidden="true"
                            sx={(theme) => ({
                              position: 'absolute',
                              zIndex: 2,
                              right: { xs: 12, sm: 24 },
                              bottom: { xs: 14, sm: 22 },
                              width: { xs: 42, sm: 54 },
                              height: { xs: 42, sm: 54 },
                              display: 'grid',
                              placeItems: 'center',
                              color: theme.palette.primary.main,
                              bgcolor: alpha(theme.palette.background.default, 0.82),
                              border: `2px solid ${theme.palette.primary.main}`,
                              borderRadius: 3,
                              boxShadow: `0 4px 0 ${darken(theme.palette.primary.main, 0.47)}, 0 0 12px ${alpha(theme.palette.primary.main, 0.4)}`,
                              transition: 'transform 160ms ease, color 160ms ease',
                            })}
                          >
                            <Image size={24} />
                          </Box>

                          <IconButton
                            aria-label={`Remove ${friend.username}`}
                            onClick={(event) => {
                              event.stopPropagation();
                              handleRemoveFriend(friend.id, friend.username);
                            }}
                            onKeyDown={(event) => event.stopPropagation()}
                            disabled={saving}
                            sx={(theme) => ({
                              position: 'absolute',
                              zIndex: 2,
                              top: 12,
                              right: 12,
                              width: 44,
                              height: 44,
                              bgcolor: alpha(theme.palette.secondary.main, 0.18),
                              color: theme.palette.secondary.main,
                              boxShadow: `0 0 10px ${alpha(theme.palette.secondary.main, 0.24)}`,
                              '&:hover': {
                                bgcolor: theme.palette.secondary.main,
                                color: theme.palette.text.primary,
                              },
                              '&.Mui-disabled': {
                                bgcolor: alpha(theme.palette.text.primary, 0.08),
                                color: alpha(theme.palette.text.primary, 0.25),
                              },
                            })}
                          >
                            <UserMinus size={20} />
                          </IconButton>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              ) : renderEmpty('No friends yet.')}
            </Box>

            <Box component="section">
              {renderSectionTitle(Send, 'Sent Invites', outgoing.length)}
              {outgoing.length ? (
                <Grid container spacing={2}>
                  {outgoing.map((request) => {
                    const friend = getFriendMetrics(request.user);

                    return (
                      <Grid size={{ xs: 12, sm: 6, md: 4 }} key={request.id}>
                        <Box
                          sx={(theme) => ({
                            ...arcadePanelSx(theme, 0.25),
                            p: 2,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                          })}
                        >
                          {renderAvatar(request.user, 72)}
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body1" sx={{ color: 'primary.main', overflowWrap: 'anywhere' }}>
                              {friend.username}
                            </Typography>
                            <Typography variant="caption" sx={(theme) => ({ color: alpha(theme.palette.text.primary, 0.55) })}>
                              Pending invite | Points {formatMetricValue(friend.score)} | {formatMetricValue(friend.completedTasks)} done
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              ) : renderEmpty('No pending sent invites.')}
            </Box>
          </Stack>
        )}
      </Stack>

    </Container>
  );
};

export default Friends;
